import { Dispatch, SetStateAction, useCallback, useState } from "react"
import { useField, useFormikContext } from "formik"
import { FinalSuccessDialogResources } from "../finalSuccessDialog/FinalSuccessDialogResources"
import { defaultOrderContextState, OrderContextState, OrderFormStatus } from "../../mainForm/helpers"
import { MeetingServiceOrder } from "../../../../../modules/merchant/accounting/accounting/accountingTypes"
import {
    CreateUpdateBooking,
    SelectedMainUserState,
    CreateOrderDialogFlow,
    CreateOrderDialogType,
    ResourceItem,
    CreateUpdateBookingOrganizer,
} from "../../createOrderTypes"
import { DeliveryTimesField, DynamicConfigKey, DynamicMainFormValues, MainOrderFormikRef } from "../form/formTypes"
import { OutlookBookingSuccessArgs } from "../../../../../modules/order/pages/outlookCreateOrderTypes"
import { defaultInitialEventState, InitialEventState } from "./useInitialEventState"
import { NullableCalendarEvent } from "./calendar/fullCalendarTypes"
import { CreateUpdateBookingResponse } from "../../createOrderApi"
import { defaultMultipleDeliveries } from "../form/formLib"
import { ResourceBooking } from "./ResourceBooking"
import { CreateOrderType } from "../../helpers"
import { getOrganizer } from "../form/helpers"

type ResourceBookingWrapperProps = Readonly<{
    confirmBookingDialog: boolean
    setConfirmBookingDialog: Dispatch<SetStateAction<boolean>>
    initialEventState: InitialEventState
    setInitialEventState: Dispatch<SetStateAction<InitialEventState>>
    handleChangeOrderType: (type: CreateOrderType) => void
    handleCancelClick: () => void
    handleClose: () => void
    user: SelectedMainUserState
    orderContext: OrderContextState
    setOrderContext: Dispatch<SetStateAction<OrderContextState>>
    orderFormStatusRef: React.MutableRefObject<OrderFormStatus>
    orderDetails?: MeetingServiceOrder
    flowConfig?: CreateOrderDialogFlow
    outerFormik: MainOrderFormikRef
    handleOutlookBookingCompleted?: (args: OutlookBookingSuccessArgs) => void
    dialogType: CreateOrderDialogType
}>

export function ResourceBookingWrapper({
    confirmBookingDialog,
    setConfirmBookingDialog,
    handleClose,
    initialEventState,
    setInitialEventState,
    handleChangeOrderType,
    handleCancelClick,
    user,
    orderContext,
    setOrderContext,
    orderFormStatusRef,
    orderDetails,
    flowConfig,
    outerFormik,
    handleOutlookBookingCompleted,
    dialogType,
}: ResourceBookingWrapperProps) {
    const [finalSuccessDialog, setFinalSuccessDialog] = useState<CreateUpdateBookingResponse | null>(null)
    const [refreshEventsToken, setRefreshEventsToken] = useState(0)

    const [{ value: event }, , eventHelpers] = useField<NullableCalendarEvent>("event")
    const [, , fullResourcesHelpers] = useField<ResourceItem[]>("fullResourcesList")
    const [{ value: multipleDeliveries }, , multipleDeliveriesHelpers] = useField<DeliveryTimesField>(
        DynamicConfigKey.MULTIPLE_DELIVERIES,
    )

    const { setValues } = useFormikContext<DynamicMainFormValues>()
    const handleChangeLocalEvent = (next: NullableCalendarEvent) => {
        eventHelpers.setValue(next)
        if (!next) {
            fullResourcesHelpers.setValue([])
        }
    }

    const setMany = useCallback(
        (patch: Partial<DynamicMainFormValues>) => {
            setValues((prev) => ({ ...prev, ...patch }))
        },
        [setValues],
    )

    const handleOpenSuccessDialog = (orderDetails: CreateUpdateBookingResponse, orderContent: CreateUpdateBooking) => {
        const { order } = orderDetails
        const { id, meetingDetails, bookingDetails, messageToServiceCenter } = order

        const resourcesList = bookingDetails?.resources ?? []

        const organizer: CreateUpdateBookingOrganizer = getOrganizer(order.organizers)

        setFinalSuccessDialog(orderDetails)
        setInitialEventState({
            event,
            initValues: {
                id,
                meetingName: meetingDetails.meetingName || "",
                organizers: organizer,
                attendees: meetingDetails.attendees || [],
                resources: resourcesList,
                messageToServiceCenter: messageToServiceCenter || "",
                isPrivate: Boolean(meetingDetails.isPrivate),
                isExternal: Boolean(meetingDetails.isExternal),
            },
            isUpdated: false,
        })

        const patch: Partial<DynamicMainFormValues> = {
            resources: resourcesList,
            [DynamicConfigKey.MEETING_NAME]: meetingDetails.meetingName || "",
            [DynamicConfigKey.REGISTER_PARTICIPANTS]: meetingDetails.attendees || [],
            [DynamicConfigKey.PRIVATE_MEETING]: Boolean(meetingDetails.isPrivate),
            [DynamicConfigKey.EXTERNAL_MEETING]: Boolean(meetingDetails.isExternal),
        }

        if (organizer) {
            patch[DynamicConfigKey.MEETING_RESPONSIBLE] = organizer
        }

        setMany(patch)

        if (!multipleDeliveries?.deliveryDate) {
            const deliveryDate = orderContent.meetingDetails.startDateTime
            multipleDeliveriesHelpers.setValue({
                ...defaultMultipleDeliveries,
                deliveryDate: deliveryDate.split(" ")[0],
            })
        }

        handleOutlookBookingCompleted &&
            handleOutlookBookingCompleted({
                orderId: id,
                start: meetingDetails.startDateTime,
                end: meetingDetails.endDateTime,
            })
    }

    const handleClearResourceIds = () => {
        handleChangeLocalEvent(null)
        setConfirmBookingDialog(false)

        if (initialEventState.initValues?.id) {
            setInitialEventState((prevState) => ({
                ...prevState,
                isUpdated: true,
            }))
        }
    }

    const handleCloseFinalSuccessDialog = (shouldRefresh = true) => {
        setFinalSuccessDialog(null)
        shouldRefresh && setRefreshEventsToken((prev) => prev + 1)
    }
    const resetBookingToInitialState = () => {
        setOrderContext(defaultOrderContextState)
        outerFormik.current?.resetForm()
        setInitialEventState(defaultInitialEventState)
        handleChangeLocalEvent(null)
    }

    return (
        <>
            <ResourceBooking
                initialEventState={initialEventState}
                handleCancelClick={handleCancelClick}
                confirmBookingDialog={confirmBookingDialog}
                setConfirmBookingDialog={setConfirmBookingDialog}
                handleOpenSuccessDialog={handleOpenSuccessDialog}
                handleClearResourceIds={handleClearResourceIds}
                user={user}
                orderContext={orderContext}
                refreshEventsToken={refreshEventsToken}
                setOrderContext={setOrderContext}
                orderDetails={orderDetails}
                orderFormStatusRef={orderFormStatusRef}
                flowConfig={flowConfig}
                dialogType={dialogType}
            />
            {finalSuccessDialog && (
                <FinalSuccessDialogResources
                    orderDetails={finalSuccessDialog}
                    onClose={handleCloseFinalSuccessDialog}
                    handleCloseMainDialog={handleClose}
                    handleOpenMeetingService={handleChangeOrderType}
                    orderFormStatusRef={orderFormStatusRef}
                    flowConfig={flowConfig}
                    resetBookingToInitialState={resetBookingToInitialState}
                />
            )}
        </>
    )
}
